














































































import { filters, grouping } from '@/config/user_evolution_report';
import MultiSelect from '@shared/components/forms/inputs/multi-select/multi-select.vue';
import { ChartType, DisplayOptions } from '@shared/interfaces/chart-interfaces';
import { SelectOptionInterface } from '@shared/interfaces/inputs';
import ChartWrapper from '@shared/layouts/wrappers/chart-wrapper.vue';
import { UserEvolutionChartData } from '@contacts/interfaces/user-evolution';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import BCChart from '@shared/components/charts/bc-chart.vue';
import Table from '@shared/components/tables/table.vue';
import { ChartData, ChartOptions } from 'chart.js';

interface UserEvolutionFilters {
  period: string;
  duration: string;
  display: string;
  acumulative: 0 | 1 | null;
}

@Component({
  name: 'bc-user-evolution-chart',
  components: {
    'bc-chart-wrapper': ChartWrapper,
    'bc-multi-select': MultiSelect,
    'bc-chart': BCChart,
    'bpc-table': Table,
  },
})
export default class UserEvolutionChart extends Vue {
  $refs!: {
    evolChart: InstanceType<typeof BCChart> & {
      renderChart(chartData: ChartData, options?: ChartOptions): void;
    };
  };

  // Props
  @Prop({ type: String, default: 'last_month' }) readonly defaultPeriod: string | null;
  @Prop({
    type: Object,
    default: {
      showChartBar: true,
      showChartLine: true,
      showChartArea: false,
      showChartPie: false,
    },
  })
  readonly displayOptions: DisplayOptions | null;

  // Data
  defaultChartType: ChartType = ChartType.LineChart;
  chartType: ChartType = ChartType.LineChart;

  filters: UserEvolutionFilters = {
    period: '',
    duration: '',
    display: '',
    acumulative: null,
  };

  durationOptions: SelectOptionInterface[] = [];

  get changeDetectorStr(): string {
    return [this.filters.period, this.filters.duration, this.filters.acumulative].join('--');
  }

  get noResults(): boolean {
    return this.userEvolution.labels.length === 0;
  }

  userEvolution: UserEvolutionChartData = {
    labels: [],
    acumulative_confirmed_count: [],
    acumulative_not_confirmed_count: [],
    confirmed_count: [],
    not_confirmed_count: [],
  };
  chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  loading: boolean = false;

  tableFields = [
    { key: 'label', label: this.$t('global.date.label') as string },
    { key: 'confirmed_count', label: this.$t('global.profiles.confirmed') as string },
    { key: 'not_confirmed_count', label: this.$t('global.profiles.not_confirmed') as string },
  ];

  currentPage: number = 1;
  itemsPerPage: number = 20;

  chartData: ChartData = {
    labels: [],
    datasets: [
      {
        data: [],
        label: this.$t('global.profiles.confirmed') as string,
        backgroundColor: '#3CD682',
        borderColor: '#3CD682 ',
        borderWidth: 1,
        hoverBackgroundColor: '#7BF5B2',
        hoverBorderColor: '#7BF5B2',
        borderCapStyle: 'round',
      },
    ],
  };

  // Computed
  // Filters

  get periodFilterOptions(): SelectOptionInterface[] {
    return filters?.period?.options || [];
  }

  get periodFilterPlaceholder(): string {
    return this.$t(`${filters?.period?.placeholder}`).toString();
  }

  get myGrouping() {
    return grouping.duration.options;
  }

  get durationGroupingOptionsChangeDetector() {
    return this.durationGroupingOptions.map(duration => duration.value).join('-');
  }

  // Grouping
  get durationGroupingOptions(): SelectOptionInterface[] {
    let filteredOptions = null;

    if (this.filters.period === 'today' || this.filters.period === 'yesterday') {
      filteredOptions = this.myGrouping.filter(option => option.value === 'per_hour');
    } else if (
      this.filters.period === 'last_7_days' ||
      this.filters.period === 'last_14_days' ||
      this.filters.period === 'last_month'
    ) {
      filteredOptions = this.myGrouping.filter(option => option.value === 'per_day');
    } else if (
      this.filters.period === 'last_trimester' ||
      this.filters.period === 'last_semester' ||
      this.filters.period === 'last_year'
    ) {
      filteredOptions = this.myGrouping.filter(
        option => option.value === 'per_week' || option.value === 'per_month',
      );
    }

    return filteredOptions || grouping?.duration?.options;
  }

  get durationGroupingPlaceholder(): string {
    return this.$t(`${grouping?.duration?.placeholder}`).toString();
  }

  get acumulativeOptions(): SelectOptionInterface[] {
    return [
      {
        text: 'global.chart.cumulative',
        value: 1,
      },
      {
        text: 'global.chart.count',
        value: 0,
      },
    ];
  }

  get tableItems() {
    return this.userEvolution.labels.map((label, index) => {
      const confirmedCount = this.filters.acumulative
        ? this.userEvolution.acumulative_confirmed_count[index]
        : this.userEvolution.confirmed_count[index];

      const notConfirmedCount = this.filters.acumulative
        ? this.userEvolution.acumulative_not_confirmed_count[index]
        : this.userEvolution.not_confirmed_count[index];

      return {
        label,
        confirmed_count: confirmedCount,
        not_confirmed_count: notConfirmedCount,
      };
    });
  }

  get totalRows(): number {
    return this.userEvolution.labels.length;
  }

  // Methods
  onChartTypeChange(chartType: ChartType) {
    this.chartType = chartType;
    this.$nextTick().then(() => {
      this.renderChart();
    });
  }

  renderChart(): void {
    this.chartData = {
      labels: this.userEvolution.labels,
      datasets: [
        {
          data: this.filters.acumulative
            ? this.userEvolution.acumulative_confirmed_count
            : this.userEvolution.confirmed_count,
          label: this.$t('global.profiles.confirmed') as string,
          backgroundColor: this.chartType === ChartType.LineChart ? 'transparent' : '#3CD682',
          borderColor: '#3CD682',
          borderWidth: 2,
          hoverBackgroundColor: '#7BF5B2',
          hoverBorderColor: '#7BF5B2',
          borderCapStyle: 'round',
        },
        {
          data: this.filters.acumulative
            ? this.userEvolution.acumulative_not_confirmed_count
            : this.userEvolution.not_confirmed_count,
          label: this.$t('global.profiles.not_confirmed') as string,
          backgroundColor: this.chartType === ChartType.LineChart ? 'transparent' : '#F0003C',
          borderColor: '#F0003C',
          borderWidth: 2,
          hoverBackgroundColor: '#EF4444',
          hoverBorderColor: '#EF4444',
          borderCapStyle: 'round',
        },
      ],
    };

    this.$refs.evolChart.renderChart(this.chartData, this.chartOptions);
  }

  async getUserEvolution() {
    this.loading = true;
    const url = this.$bpcApiRouteHelper.getEntityPath('user-evolution');
    const filters = this.filters;
    this.$bpcApi
      .get(url, {
        params: {
          ...filters,
        },
      })
      .subscribe(res => {
        this.loading = false;
        this.userEvolution = res.data;
        this.renderChart();
      });
  }

  getUserEvolutionIfStopChanging() {
    const changeStr = this.changeDetectorStr;
    setTimeout(() => {
      if (changeStr === this.changeDetectorStr) {
        this.getUserEvolution();
      }
    }, 300);
  }

  // LIFECYCLE
  // created(){}
  mounted() {
    this.filters.period = this.defaultPeriod;
  }

  // updated(){}
  // destroyed(){}

  // Watchers
  @Watch('filters.period')
  onFilterPeriodChange(newVal: string, oldVal: string) {
    if (this.filters.period === 'today' || this.filters.period === 'yesterday') {
      this.filters.duration = 'per_hour';
    } else if (
      this.filters.period === 'last_7_days' ||
      this.filters.period === 'last_14_days' ||
      this.filters.period === 'last_month'
    ) {
      this.filters.duration = 'per_day';
    } else if (
      this.filters.period === 'last_trimester' ||
      this.filters.period === 'last_semester' ||
      this.filters.period === 'last_year'
    ) {
      this.filters.duration = 'per_month';
    }
  }

  @Watch('changeDetectorStr')
  onChangeDetectorStrChange(val) {
    if (val) {
      this.getUserEvolutionIfStopChanging();
    }
  }
}
